import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaCalendarDay, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const SEVERIDAD_COLORES = {
    baja: '#10B981',
    media: '#F59E0B',
    alta: '#EF4444',
    critica: '#7F1D1D'
};

const SEVERIDAD_BG = {
    baja: 'bg-green-100 text-green-800',
    media: 'bg-yellow-100 text-yellow-800',
    alta: 'bg-red-100 text-red-800',
    critica: 'bg-red-900 text-red-100'
};

const ESTADO_CLASES = {
    abierto: 'bg-blue-100 text-blue-800',
    en_progreso: 'bg-amber-100 text-amber-800',
    resuelto: 'bg-green-100 text-green-800',
    cerrado: 'bg-gray-100 text-gray-600'
};

const ESTADO_LABELS = {
    abierto: 'Abierto',
    en_progreso: 'En progreso',
    resuelto: 'Resuelto',
    cerrado: 'Cerrado'
};

/**
 * Obtiene los días del mes organizados en semanas (Lun-Dom).
 * Incluye días del mes anterior/siguiente para rellenar la cuadrícula.
 */
const obtenerDiasDelMes = (anio, mes) => {
    const primerDia = new Date(anio, mes, 1);
    const ultimoDia = new Date(anio, mes + 1, 0);

    // getDay() retorna 0=Dom, 1=Lun... Convertir a 0=Lun, 6=Dom
    let inicioSemana = primerDia.getDay() - 1;
    if (inicioSemana < 0) inicioSemana = 6;

    const dias = [];

    // Días del mes anterior
    const mesAnteriorUltimoDia = new Date(anio, mes, 0).getDate();
    for (let i = inicioSemana - 1; i >= 0; i--) {
        dias.push({
            dia: mesAnteriorUltimoDia - i,
            mesActual: false,
            fecha: formatearFecha(anio, mes - 1, mesAnteriorUltimoDia - i)
        });
    }

    // Días del mes actual
    for (let d = 1; d <= ultimoDia.getDate(); d++) {
        dias.push({
            dia: d,
            mesActual: true,
            fecha: formatearFecha(anio, mes, d)
        });
    }

    // Días del mes siguiente para completar la última semana
    const restante = 7 - (dias.length % 7);
    if (restante < 7) {
        for (let d = 1; d <= restante; d++) {
            dias.push({
                dia: d,
                mesActual: false,
                fecha: formatearFecha(anio, mes + 1, d)
            });
        }
    }

    return dias;
};

const formatearFecha = (anio, mes, dia) => {
    const fecha = new Date(anio, mes, dia);
    const a = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    return `${a}-${m}-${d}`;
};

const hoyStr = () => {
    const hoy = new Date();
    return formatearFecha(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
};

const EventosCalendario = () => {
    const ahora = new Date();
    const [mes, setMes] = useState(ahora.getMonth()); // 0-indexed
    const [anio, setAnio] = useState(ahora.getFullYear());
    const [eventos, setEventos] = useState({});
    const [loading, setLoading] = useState(true);
    const [diaSeleccionado, setDiaSeleccionado] = useState(null);

    const cargarEventos = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/eventos/calendario', {
                params: { mes: mes + 1, anio } // API espera mes 1-indexed
            });
            setEventos(res.data.eventos || {});
        } catch (error) {
            console.error('Error al cargar calendario:', error);
            setEventos({});
        } finally {
            setLoading(false);
        }
    }, [mes, anio]);

    useEffect(() => {
        cargarEventos();
    }, [cargarEventos]);

    const mesAnterior = () => {
        setDiaSeleccionado(null);
        if (mes === 0) {
            setMes(11);
            setAnio(anio - 1);
        } else {
            setMes(mes - 1);
        }
    };

    const mesSiguiente = () => {
        setDiaSeleccionado(null);
        if (mes === 11) {
            setMes(0);
            setAnio(anio + 1);
        } else {
            setMes(mes + 1);
        }
    };

    const irAHoy = () => {
        const hoy = new Date();
        setMes(hoy.getMonth());
        setAnio(hoy.getFullYear());
        setDiaSeleccionado(null);
    };

    const seleccionarDia = (diaInfo) => {
        if (!diaInfo.mesActual) return;
        setDiaSeleccionado(
            diaSeleccionado === diaInfo.fecha ? null : diaInfo.fecha
        );
    };

    const dias = obtenerDiasDelMes(anio, mes);
    const hoy = hoyStr();
    const eventosDelDiaSeleccionado = diaSeleccionado ? (eventos[diaSeleccionado] || []) : [];

    return (
        <div className="max-w-5xl mx-auto">
            {/* Navegación del mes */}
            <div className="flex items-center justify-between mb-6">
                <button
                    onClick={mesAnterior}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-gray-900"
                >
                    <FaChevronLeft size={18} />
                </button>

                <div className="flex items-center gap-3">
                    <motion.h2
                        key={`${mes}-${anio}`}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-2xl font-bold text-gray-800"
                    >
                        {MESES[mes]} {anio}
                    </motion.h2>
                    {(mes !== ahora.getMonth() || anio !== ahora.getFullYear()) && (
                        <button
                            onClick={irAHoy}
                            className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                        >
                            Hoy
                        </button>
                    )}
                </div>

                <button
                    onClick={mesSiguiente}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-gray-900"
                >
                    <FaChevronRight size={18} />
                </button>
            </div>

            {/* Cabecera de días de la semana */}
            <div className="grid grid-cols-7 mb-1">
                {DIAS_SEMANA.map((dia) => (
                    <div
                        key={dia}
                        className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider py-2"
                    >
                        {dia}
                    </div>
                ))}
            </div>

            {/* Cuadrícula del calendario */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                </div>
            ) : (
                <motion.div
                    key={`${mes}-${anio}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="grid grid-cols-7 border-t border-l border-gray-200 rounded-lg overflow-hidden"
                >
                    {dias.map((diaInfo, idx) => {
                        const eventosDelDia = eventos[diaInfo.fecha] || [];
                        const tieneEventos = eventosDelDia.length > 0;
                        const esHoy = diaInfo.fecha === hoy;
                        const esSeleccionado = diaInfo.fecha === diaSeleccionado;

                        return (
                            <div
                                key={idx}
                                onClick={() => seleccionarDia(diaInfo)}
                                className={`
                                    relative border-r border-b border-gray-200 min-h-[80px] p-2
                                    transition-colors cursor-pointer
                                    ${diaInfo.mesActual ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50'}
                                    ${esHoy ? 'ring-2 ring-inset ring-blue-400' : ''}
                                    ${esSeleccionado ? 'bg-blue-50' : ''}
                                `}
                            >
                                {/* Número del día */}
                                <span
                                    className={`
                                        text-sm font-medium
                                        ${!diaInfo.mesActual ? 'text-gray-300' : ''}
                                        ${esHoy && diaInfo.mesActual ? 'text-blue-600 font-bold' : ''}
                                        ${diaInfo.mesActual && !esHoy ? 'text-gray-700' : ''}
                                    `}
                                >
                                    {diaInfo.dia}
                                </span>

                                {/* Dots de eventos */}
                                {tieneEventos && diaInfo.mesActual && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {eventosDelDia.slice(0, 5).map((ev, i) => (
                                            <div
                                                key={i}
                                                className="w-2 h-2 rounded-full"
                                                style={{ backgroundColor: SEVERIDAD_COLORES[ev.severidad] || '#6B7280' }}
                                                title={ev.titulo}
                                            />
                                        ))}
                                        {eventosDelDia.length > 5 && (
                                            <span className="text-[10px] text-gray-400 leading-none self-center">
                                                +{eventosDelDia.length - 5}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Contador de eventos */}
                                {tieneEventos && diaInfo.mesActual && (
                                    <div className="absolute bottom-1 right-1">
                                        <span className="text-[10px] text-gray-400">
                                            {eventosDelDia.length}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </motion.div>
            )}

            {/* Panel de detalle del día seleccionado */}
            <AnimatePresence>
                {diaSeleccionado && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        className="overflow-hidden"
                    >
                        <div className="mt-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                            {/* Cabecera del panel */}
                            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                                <div className="flex items-center gap-2 text-gray-700">
                                    <FaCalendarDay size={14} className="text-blue-500" />
                                    <span className="font-semibold text-sm">
                                        {(() => {
                                            const [a, m, d] = diaSeleccionado.split('-');
                                            return `${parseInt(d)} de ${MESES[parseInt(m) - 1]} ${a}`;
                                        })()}
                                    </span>
                                    {eventosDelDiaSeleccionado.length > 0 && (
                                        <span className="text-xs text-gray-400 ml-1">
                                            ({eventosDelDiaSeleccionado.length} evento{eventosDelDiaSeleccionado.length !== 1 ? 's' : ''})
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => setDiaSeleccionado(null)}
                                    className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                                >
                                    &times;
                                </button>
                            </div>

                            {/* Lista de eventos o estado vacío */}
                            <div className="p-4">
                                {eventosDelDiaSeleccionado.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <FaExclamationTriangle size={24} className="mx-auto mb-2 opacity-40" />
                                        <p className="text-sm">Sin eventos este día</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {eventosDelDiaSeleccionado.map((evento) => (
                                            <motion.div
                                                key={evento.id}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                                            >
                                                {/* Hora */}
                                                <div className="flex items-center gap-1 text-gray-400 min-w-[60px]">
                                                    <FaClock size={10} />
                                                    <span className="text-xs font-mono">
                                                        {evento.hora || '--:--'}
                                                    </span>
                                                </div>

                                                {/* Indicador de categoría */}
                                                <div
                                                    className="w-1 h-8 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: evento.categoria_color || '#6B7280' }}
                                                />

                                                {/* Título y categoría */}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 truncate">
                                                        {evento.titulo}
                                                    </p>
                                                    {evento.categoria && (
                                                        <p className="text-xs text-gray-400 mt-0.5">
                                                            {evento.categoria}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Badges */}
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <span
                                                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${SEVERIDAD_BG[evento.severidad] || 'bg-gray-100 text-gray-600'}`}
                                                    >
                                                        {evento.severidad}
                                                    </span>
                                                    <span
                                                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${ESTADO_CLASES[evento.estado] || 'bg-gray-100 text-gray-600'}`}
                                                    >
                                                        {ESTADO_LABELS[evento.estado] || evento.estado}
                                                    </span>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default EventosCalendario;
